import queryPart from '../utils/lrp/urlParts/queryPart';
import categoriesPart from '../utils/lrp/urlParts/categoriesPart';
import sellerPart from '../utils/lrp/urlParts/sellerPart';
import pageIndexPart from '../utils/lrp/urlParts/pageIndexPart';
import attributesPart from '../utils/lrp/urlParts/attributesPart';
import getSortedAttributes from '../utils/lrp/getSortedAttributes';
import hashPart from '../utils/lrp/urlParts/hashPart';
import formatQuery from '../utils/lrp/formatQuery';
import urlPrefixes from '../constants/urlPrefixes';
import getRangeAttributeKeyValuePair from '../utils/lrp/getRangeAttributeKeyValuePair';
import includeViewOptions from '../utils/lrp/includeViewOptions';
import createSavedSearchAttrs from '../utils/lrp/savedSearchesAttributes';
import sortAttributeNames from '../constants/sortAttributeNames';
import getURLQueryString, { TURLQueryParams } from '../utils/lrp/urlParts/getURLQueryString';

type TLRPUrlParams = {
  asSavedSearch?: boolean;
  attributes?: {};
  attributesByKey?: {
    attributeKey: string;
    attributeValueKey: string;
  }[];
  bypassSpellingSuggestion?: boolean;
  categories?: {
    l1Category?: {
      key?: string;
    };
    l2Category?: {
      key?: string;
    };
  };
  distance?: string | number;
  isViewOptionsClicked?: boolean;
  pageIndex?: number;
  pageSize?: string | number;
  postcode?: string;
  query?: string;
  rangeAttributes?: {
    attributeKey: string;
    from: string | number | null;
    to: string | number;
  }[];
  removeAllFilters?: boolean;
  removeAllAttributes?: boolean;
  searchInTitleAndDescription?: boolean;
  seller?: {
    id: number | string;
    name: string;
  };
  seoFriendlyAttributes?: string[];
  showQueryInSeoFriendlyPart?: boolean;
  sortOptions?: {
    sortBy: string;
    sortOrder: string;
    sortAttribute: string | null;
  };
  traits?: string[];
  urlQueryParameters?: TURLQueryParams;
  view?: string;
};

/**
 * Generates LRP urls
 * Prefer to use server-side only, client bundle is big because of `seoUrlPart()`
 */
export default ({
  asSavedSearch,
  attributes = {},
  attributesByKey,
  bypassSpellingSuggestion,
  categories,
  distance = 0,
  isViewOptionsClicked,
  pageIndex,
  pageSize = 0,
  postcode,
  query,
  rangeAttributes,
  removeAllAttributes = false,
  removeAllFilters = false,
  searchInTitleAndDescription = false,
  seller,
  seoFriendlyAttributes,
  showQueryInSeoFriendlyPart = true,
  sortOptions,
  traits,
  urlQueryParameters,
  view,
}: TLRPUrlParams): string => {
  const keepAttributes = !removeAllFilters && !removeAllAttributes;
  const sortedAttributes = getSortedAttributes(attributes, seoFriendlyAttributes);

  const queryUrl = showQueryInSeoFriendlyPart ? queryPart(query) : '';
  const categoriesUrl = !removeAllFilters ? categoriesPart(categories) : '';
  const sellerUrl = sellerPart(seller);
  const createAttributesUrl = keepAttributes ? attributesPart(sortedAttributes.seoFriendlyAttributes) : '';
  const pageIndexUrl = pageIndexPart(pageIndex);
  const urlQueryString = getURLQueryString(urlQueryParameters);

  // hash friendly part
  const rangeAttributesData = rangeAttributes ? rangeAttributes.map(getRangeAttributeKeyValuePair) : [];
  const savedSearch = asSavedSearch;

  // dynamic attributes hash part-> provided by buyside-api
  const hashFriendlyAttributes = keepAttributes && sortedAttributes.hashFriendlyAttributes;
  const hashFriendlyRangeAttributes = keepAttributes && Object.assign({}, ...rangeAttributesData);
  const hashFriendlySavedSearch = keepAttributes && savedSearch && createSavedSearchAttrs(attributesByKey);

  const hashedPart = hashPart({
    [urlPrefixes.query]: !showQueryInSeoFriendlyPart && query ? formatQuery(query) : null,
    [sortAttributeNames.SORT_BY]: keepAttributes && sortOptions?.sortBy ? sortOptions?.sortBy : null,
    [sortAttributeNames.SORT_ORDER]: keepAttributes && sortOptions?.sortOrder ? sortOptions?.sortOrder : null,
    [sortAttributeNames.SORT_ATTRIBUTE]:
      keepAttributes && sortOptions?.sortAttribute ? sortOptions?.sortAttribute : null,
    [urlPrefixes.distance]: !removeAllFilters && Number(distance) > 0 ? distance : null,
    [urlPrefixes.postcode]: !removeAllFilters && postcode ? postcode.toUpperCase() : null,
    [urlPrefixes.searchInTitleAndDescription]: !removeAllFilters && searchInTitleAndDescription ? true : null,
    [urlPrefixes.view]: (!removeAllFilters || query) && includeViewOptions(view, isViewOptionsClicked) ? view : null,
    [urlPrefixes.Limit]: Number(pageSize) > 0 ? pageSize : null,
    [urlPrefixes.bypassSpellingSuggestion]: bypassSpellingSuggestion ? true : null,
    [urlPrefixes.traits]: !removeAllFilters && traits && traits.length > 0 ? traits : null,
    ...hashFriendlyAttributes,
    ...hashFriendlyRangeAttributes,
    ...hashFriendlySavedSearch,
  });
  const hashedUrl = hashedPart ? `${urlPrefixes.nonTrackingKey}${hashedPart}` : '';

  return `${sellerUrl}${categoriesUrl}${queryUrl}${createAttributesUrl}${pageIndexUrl}/${urlQueryString}${hashedUrl}`;
};
